import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Menu, X } from 'lucide-react';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [mobileMenuOpen]);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  return (
    <>
      {/* Skip Navigation Link for Screen Readers */}
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 bg-violet-600 text-white px-4 py-2 rounded-md z-50"
      >
        Skip to main content
      </a>
      
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-violet-100" role="banner">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <div className="flex items-center gap-2">
              <img src="/nursebloc-logo.png" alt="NurseBloc Logo" width="32" height="32" className="rounded-lg" />
              <span className="text-xl font-bold text-violet-600">NurseBloc</span>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8" role="navigation" aria-label="Main navigation">
              <button 
                onClick={() => scrollToSection('how-it-works')}
                className="text-violet-600 hover:text-violet-800 transition-colors text-sm font-medium"
              >
                How It Works
              </button>
              <button 
                onClick={() => scrollToSection('services')}
                className="text-violet-600 hover:text-violet-800 transition-colors text-sm font-medium"
              >
                Services
              </button>
              <button 
                onClick={() => scrollToSection('why-nurse')}
                className="text-violet-600 hover:text-violet-800 transition-colors text-sm font-medium"
              >
                The Difference
              </button>
              <button 
                onClick={() => scrollToSection('careers')}
                className="text-violet-600 hover:text-violet-800 transition-colors text-sm font-medium"
              >
                Careers
              </button>
            </nav>

            {/* CTA Button */}
            <div className="hidden md:block">
              <Button 
                className="btn-nursebloc px-6 py-2 rounded-full font-medium shadow-lg"
                onClick={() => scrollToSection('contact')}
                aria-label="Schedule a free nurse consultation"
              >
                Talk to a Nurse
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2 text-violet-600"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-violet-100">
            <nav className="px-4 py-6 space-y-4" role="navigation" aria-label="Mobile navigation">
              <button 
                onClick={() => scrollToSection('how-it-works')}
                className="block w-full text-violet-600 hover:text-violet-800 text-left py-2 font-medium"
              >
                How It Works
              </button>
              <button 
                onClick={() => scrollToSection('services')}
                className="block w-full text-violet-600 hover:text-violet-800 text-left py-2 font-medium"
              >
                Services
              </button>
              <button 
                onClick={() => scrollToSection('why-nurse')}
                className="block w-full text-violet-600 hover:text-violet-800 text-left py-2 font-medium"
              >
                The Difference
              </button>
              <button 
                onClick={() => scrollToSection('careers')}
                className="block w-full text-violet-600 hover:text-violet-800 text-left py-2 font-medium"
              >
                Careers
              </button>
              <Button 
                className="btn-nursebloc w-full rounded-full mt-2"
                onClick={() => scrollToSection('contact')}
                aria-label="Schedule a free nurse consultation"
              >
                Talk to a Nurse
              </Button>
            </nav>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;
